import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { X } from 'lucide-react';

import { isMarkdownAttachment } from '../lib/attachments';
import { logError } from '../lib/logger';
import { markdownAttachmentSourceForAttachment } from '../lib/markdownAttachment';
import {
  MediaAttachmentStates,
  isComposerOwnedMediaAttachment,
  isUploadInflightState,
  removeMediaAttachment,
  retryMediaAttachmentsByUploadIds,
  sortByTimestamp,
  useAllMediaAttachments,
} from '../lib/media_attachments_store';
import { cn } from '../lib/utils';

import SpinnerIcon from '../../assets/images/spinner.svg';

import { clampUploadProgress, getMediaAttachmentFileName } from './MediaAttachmentUploadPreview';
import MarkdownAttachment from './messages/MarkdownAttachment';
import MediaAttachmentThumbnail from './shared/MediaAttachmentThumbnail';
import { Tooltip } from './shared/Tooltip';

const processingLabelFor = (contentType) => {
  if (contentType === 'application/pdf') return 'Preparing PDF';
  if (contentType?.startsWith('video/')) return 'Processing video';
  if (contentType?.startsWith('audio/')) return 'Processing audio';
  return null;
};

// Figma composer/inputs image tile: 160px rounded-16 cover thumbnail, the checkbox-spec
// black close chip top-right, and while the upload is in flight a dark scrim plus a
// black percentage pill top-left.
const ComposerMediaAttachmentTile = ({ mediaAttachment, onRemove, onRetry }) => {
  const uploadState = mediaAttachment?.upload?.state;
  const progress = clampUploadProgress(
    mediaAttachment?.upload?.progress ?? (uploadState === MediaAttachmentStates.completed ? 100 : 0),
  );
  const isUploading = isUploadInflightState(uploadState);
  const isRetrying = uploadState === MediaAttachmentStates.retrying;
  const isError = uploadState === MediaAttachmentStates.error;
  const processingLabel =
    uploadState === MediaAttachmentStates.processing ? processingLabelFor(mediaAttachment?.metadata?.content_type) : null;
  const isMarkdown = isMarkdownAttachment(mediaAttachment);
  const markdownSource = useMemo(
    () =>
      markdownAttachmentSourceForAttachment({
        id: mediaAttachment?.id,
        public_url: mediaAttachment?.public_url,
        upload: uploadState ? { state: uploadState } : null,
      }),
    [mediaAttachment?.id, mediaAttachment?.public_url, uploadState],
  );

  const handleRemove = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onRemove(mediaAttachment.id);
    },
    [mediaAttachment.id, onRemove],
  );

  const handleRetry = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      onRetry(mediaAttachment.id);
    },
    [mediaAttachment.id, onRetry],
  );

  const fileName = getMediaAttachmentFileName(mediaAttachment);

  return (
    <Tooltip label={fileName} placement="top" className="shrink-0">
      <div className={cn('relative size-40 overflow-hidden rounded-2xl', isRetrying && 'outline outline-1 outline-yellow-400')}>
        {isMarkdown ? (
          <MarkdownAttachment
            fileName={fileName}
            showUploadStatus={false}
            size="size-40"
            source={markdownSource}
          />
        ) : (
          <MediaAttachmentThumbnail
            mediaAttachment={mediaAttachment}
            size="size-40"
            rounded="rounded-2xl"
            objectFit="cover"
            alt={fileName}
          />
        )}

        {(isUploading || isRetrying) && <div className="pointer-events-none absolute inset-0 rounded-2xl bg-black/50" />}
        {processingLabel && (
          <span className="absolute top-2 left-2 flex h-5 max-w-28 items-center gap-1 rounded-full bg-black/90 px-1.5 font-mono text-xs font-semibold text-white">
            <SpinnerIcon className="size-3 shrink-0 animate-spin" />
            <span className="truncate">{processingLabel}</span>
          </span>
        )}
        {isError && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-black/50 px-1 text-center text-white">
            <span className="text-2xs leading-tight" title={mediaAttachment?.error || undefined}>
              Upload failed
            </span>
            <button
              type="button"
              data-app-region="none"
              onClick={handleRetry}
              className="pointer-events-auto flex h-5 cursor-pointer items-center rounded-full bg-black/90 px-2 font-mono text-xs font-semibold hover:bg-black"
            >
              Retry
            </button>
          </div>
        )}

        {(isUploading || isRetrying) && (
          <span className="absolute top-2 left-2 flex h-5 items-center rounded-full bg-black/90 px-1.5 font-mono text-xs font-semibold text-white">
            {progress}%
          </span>
        )}

        <button
          type="button"
          data-app-region="none"
          onClick={handleRemove}
          className="absolute top-2 right-2 flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-black text-white hover:bg-black/80 focus:outline-none"
          aria-label="Remove attachment"
        >
          <X size={12} strokeWidth={1.5} absoluteStrokeWidth />
        </button>
      </div>
    </Tooltip>
  );
};

const RichdownComposerMediaAttachments = ({ channelId, sessionKey = undefined, roundedTop = true }) => {
  const mediaAttachmentsMap = useAllMediaAttachments({ channel_id: channelId, session_key: sessionKey });
  const mediaAttachments = useMemo(
    () =>
      Array.from(mediaAttachmentsMap.values())
        .filter((mediaAttachment) => isComposerOwnedMediaAttachment(mediaAttachment))
        .sort(sortByTimestamp('written_at')),
    [mediaAttachmentsMap],
  );

  const scrollContainerRef = useRef(null);
  const prevLengthRef = useRef(mediaAttachments.length);
  const isInitialMount = useRef(true);

  useLayoutEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevLengthRef.current = mediaAttachments.length;
      return;
    }

    // Only scroll if we already had items (not initial load from 0 to N)
    if (prevLengthRef.current > 0 && mediaAttachments.length > prevLengthRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
    prevLengthRef.current = mediaAttachments.length;
  }, [mediaAttachments.length]);

  const handleRemove = useCallback((mediaAttachmentId) => {
    removeMediaAttachment(mediaAttachmentId).catch((error) => {
      logError('RichdownComposerMediaAttachments', 'Failed to remove media attachment', error);
    });
  }, []);

  const handleRetry = useCallback((mediaAttachmentId) => {
    retryMediaAttachmentsByUploadIds([mediaAttachmentId]).catch((error) => {
      logError('RichdownComposerMediaAttachments', 'Failed to retry media attachment', error);
    });
  }, []);

  if (mediaAttachments.length === 0) return null;

  return (
    // Same tinted strip surface as the reply/edit bar (composer-mode-bar rules in
    // application.css); roundedTop is dropped when a mode bar already caps the composer.
    <div
      ref={scrollContainerRef}
      className={cn('composer-mode-bar flex gap-1.5 overflow-x-auto p-1.5', roundedTop && 'rounded-t-3xl')}
    >
      {mediaAttachments.map((mediaAttachment) => (
        <ComposerMediaAttachmentTile
          key={`media-attachment-${mediaAttachment.id}`}
          mediaAttachment={mediaAttachment}
          onRemove={handleRemove}
          onRetry={handleRetry}
        />
      ))}
    </div>
  );
};

export default RichdownComposerMediaAttachments;
